import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { bool, func, string } from "prop-types";
import cx from "classnames";

import { useStatic, usePortal } from "../../utils/hooks";
import canUseDom from "../../utils/canUseDom";

import ModalStatic from "./Modal.static";

const CLASS_ROOT = "modal-dialog";

export const ModalContext = React.createContext({});

const propTypes = {
  /** Html id attribute. */
  id: string.isRequired,
  /** Label is used as `aria-label` attribute. Instead of `label`, `labelledBy` prop can be used. */
  label: string,
  /** Id of element which describes modal. Id is passed to `aria-labelledby`. Instead of `labelledBy`, `label` prop can be used. */
  labelledBy: string,
  /** Disable plugin initialization. */
  noInit: bool,
  /** On hide callback function. */
  onHide: func,
  /** On show callback function. */
  onShow: func,
};

const Modal = ({
  className,
  children,
  id,
  label,
  labelledBy,
  onShow = () => {},
  onHide = () => {},
  noInit,
  ...other
}) => {
  const target = usePortal("root-modals");

  const [modalRef, instance] = useStatic(ModalStatic, {
    onShow,
    onHide,
    portal: false,
  });

  useEffect(() => {
    instance.current.show();
  }, [instance]);

  const classes = cx(CLASS_ROOT, className);

  const titleId = `${id}-title`;

  const render = (
    <ModalContext.Provider value={{ titleId }}>
      <div
        id={id}
        {...(!noInit && { "data-modal": "" })}
        className="modal"
        ref={modalRef}
        {...other}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        aria-labelledby={labelledBy || titleId}
      >
        <div
          className={`${CLASS_ROOT}__overlay`}
          tabIndex="-1"
          data-a11y-dialog-hide
        />
        <div className={classes} role="dialog" {...other}>
          {children}
        </div>
      </div>
    </ModalContext.Provider>
  );

  if (canUseDom() && target) {
    return createPortal(render, target);
  }

  return render;
};

Modal.propTypes = propTypes;
Modal.displayName = "Modal";

export default Modal;
